import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import { PageHeader } from "@/components/PageHeader";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Download, FileText, Receipt, Calendar } from "lucide-react";

export default function ClientFacturasPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/facturas");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const download = async (r) => {
    try {
      const res = await api.get(`/facturas/${r.id}/download`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = r.filename || `Factura_${r.folio || r.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Error al descargar");
    }
  };

  // Group by mes (YYYY-MM) descending
  const grouped = items.reduce((acc, it) => {
    const key = it.mes || "sin-mes";
    (acc[key] = acc[key] || []).push(it);
    return acc;
  }, {});
  const months = Object.keys(grouped).sort().reverse();

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto p-6 md:p-10">
        <PageHeader
          eyebrow="Documentos"
          title="Mis Facturas"
          description="Facturas de servicio emitidas por EXIS Pharma. Descarga los archivos PDF originales."
        />

        {loading && (
          <div className="bg-white border border-slate-200 p-10 text-center text-slate-500">
            Cargando...
          </div>
        )}

        {!loading && items.length === 0 && (
          <div className="bg-white border border-slate-200 p-12 text-center">
            <Receipt className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <div className="text-slate-500">
              Aún no has recibido facturas emitidas.
            </div>
            <div className="text-xs text-slate-400 mt-2">
              EXIS Pharma publicará aquí las facturas de servicio a medida que las emita.
            </div>
          </div>
        )}

        {!loading && months.length > 0 && (
          <div className="space-y-6">
            {months.map((mes) => (
              <section
                key={mes}
                data-testid={`client-factura-month-${mes}`}
                className="bg-white border border-slate-200"
              >
                <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-3 bg-slate-50">
                  <Calendar className="w-4 h-4 text-[#D95A40]" />
                  <div className="font-display font-bold text-[#0B1B3D]">
                    {mes === "sin-mes" ? "Sin mes asignado" : mes}
                  </div>
                  <div className="text-xs text-slate-500 tabular-nums ml-auto">
                    {grouped[mes].length} factura(s)
                  </div>
                </div>
                <ul className="divide-y divide-slate-100">
                  {grouped[mes].map((r) => (
                    <li
                      key={r.id}
                      data-testid={`client-factura-row-${r.id}`}
                      className="p-4 md:px-6 hover:bg-slate-50 transition-colors flex items-center justify-between gap-4 flex-wrap"
                    >
                      <div className="flex items-center gap-4 min-w-0">
                        <div className="w-11 h-11 bg-red-100 text-red-700 flex items-center justify-center shrink-0">
                          <FileText className="w-5 h-5" strokeWidth={2.2} />
                        </div>
                        <div className="min-w-0">
                          <div className="font-display font-bold text-[#0B1B3D] truncate">
                            {r.filename}
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1">
                            {r.folio && (
                              <span>
                                Folio:{" "}
                                <span className="text-[#D95A40] font-bold">
                                  {r.folio}
                                </span>
                              </span>
                            )}
                            <span>
                              {((r.size_bytes || 0) / 1024).toFixed(1)} KB
                            </span>
                            <span>
                              Recibida{" "}
                              {new Date(r.uploaded_at).toLocaleString("es-CL")}
                            </span>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => download(r)}
                        data-testid={`client-factura-download-${r.id}`}
                        className="flex items-center gap-2 px-4 py-2.5 bg-[#D95A40] hover:bg-[#c94d33] text-white text-xs font-bold uppercase tracking-wider transition-colors shrink-0"
                      >
                        <Download className="w-4 h-4" /> Descargar
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}
